"use client";

import { UI_LOCALE_OPTIONS } from "@sum-video/shared/i18n/uiLocales";
import { cn } from "@sum-video/shared/utils/cn";
import { ChevronDown, Globe } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { type JSX, Suspense } from "react";
import { useRouter } from "@/i18n/navigation";
import { useLocaleSwitchHref } from "@/i18n/useLocaleSwitchHref";

interface LocaleSwitcherProps {
  appearance?: "default" | "hero";
}

/**
 * A native <select> laid over the visible trigger: the OS picker handles the
 * menu, so no popover library ships with every page's header.
 */
function LocaleSwitcherContent({
  appearance = "default",
}: LocaleSwitcherProps): JSX.Element {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("LocaleSwitcher");
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const href = useLocaleSwitchHref(query);
  const isHero = appearance === "hero";

  const handleLocaleChange = (newLocale: string): void => {
    router.push(href, { locale: newLocale });
  };

  const currentLocale = UI_LOCALE_OPTIONS.find((loc) => loc.code === locale);

  return (
    <div
      className={cn(
        "relative inline-flex w-fit items-center gap-2 rounded-md has-[select:focus-visible]:ring-[3px] has-[select:focus-visible]:ring-ring/50",
        isHero
          ? "h-7 px-2.5 py-1 text-base font-semibold text-white hover:bg-white/10"
          : "h-9 px-3 text-base hover:bg-accent/50",
      )}
    >
      <Globe
        aria-hidden
        className={
          isHero ? "h-[18px] w-[18px]" : "h-4 w-4 text-muted-foreground"
        }
      />
      {currentLocale &&
        (isHero ? (
          <span className="uppercase">{currentLocale.code}</span>
        ) : (
          <span className="flex items-center space-x-1">
            <span>{currentLocale.flag}</span>
            <span>{currentLocale.name}</span>
          </span>
        ))}
      {isHero ? null : (
        <ChevronDown aria-hidden className="h-4 w-4 opacity-50" />
      )}
      <select
        aria-label={t("label")}
        value={locale}
        onChange={(event) => handleLocaleChange(event.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {UI_LOCALE_OPTIONS.map((loc) => (
          <option key={loc.code} value={loc.code}>
            {loc.flag} {loc.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export function LocaleSwitcher({
  appearance = "default",
}: LocaleSwitcherProps): JSX.Element {
  return (
    <Suspense
      fallback={<div className="h-9 w-20 animate-pulse rounded bg-muted" />}
    >
      <LocaleSwitcherContent appearance={appearance} />
    </Suspense>
  );
}
